import { fillTimelineGaps } from '../../utils/timeline-gaps';
import { STATE_COLORS } from '../../utils/machine-icon';
import { normalizeDashboardRange } from '../../utils/dashboard-range';
import React, { useContext, useEffect, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import CanvasJSReact from '@canvasjs/react-charts';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import { useTheme } from '@mui/material/styles';
import { GlobalContext } from '../../context/userContext';
import { AxiosInstanceLongTimeOut } from '@service/axios-instance';

export type TimelineRangeDataPoint = {
  x: number; // Row index (1 = Heartbeats, 2 = Events)
  y: [number, number]; // [Start timestamp, End timestamp]
  color?: string;
  label?: string; // row label
  toolTipContent?: string;
};

export type TimelineRangeSeries = {
  type: 'rangeBar';
  name?: string;
  dataPoints: TimelineRangeDataPoint[];
};

interface HeartbeatTimelineChartProps {
  fillGaps?: boolean;
  TITLE: string;
  DATA: TimelineRangeDataPoint[];
  startDate?: string;
  endDate?: string;
  onDateRangeChange?: (start: string, end: string) => void;
  itemsPerPage?: number;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  filterText?: string;
  onFilterChange?: (text: string) => void;
}

const HeartbeatTimelineChart: React.FC<HeartbeatTimelineChartProps> = ({ fillGaps = false, TITLE, DATA, startDate, endDate, onDateRangeChange, itemsPerPage = 25, page: externalPage, totalPages: externalTotalPages, onPageChange, filterText: externalFilterText, onFilterChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { operation } = useContext(GlobalContext);
  const [shiftTimes, setShiftTimes] = useState<string[]>([]);
  const [internalFilterText, setInternalFilterText] = useState('');
  const [internalPage, setInternalPage] = useState(1);
  const [currentNow, setCurrentNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setCurrentNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  const filterText = externalFilterText !== undefined ? externalFilterText : internalFilterText;
  const page = externalPage !== undefined ? externalPage : internalPage;

  const handlePageChange = (newPage: number) => {
    if (onPageChange) onPageChange(newPage);
    else setInternalPage(newPage);
  };

  const handleFilterChange = (newText: string) => {
    if (onFilterChange) onFilterChange(newText);
    else setInternalFilterText(newText);
  };

  useEffect(() => {
    if (operation) {
      AxiosInstanceLongTimeOut.get(`/operations/current_config?db=${operation}`, { withCredentials: true })
        .then((res: any) => {
          if (res.data && res.data.SHIFT_TIMES) {
            setShiftTimes(res.data.SHIFT_TIMES);
          }
        })
        .catch((err: any) => console.log('Error fetching operation config:', err));
    }
  }, [operation]);

  useEffect(() => {
    // Reset page to 1 when filter changes or data updates
    if (externalPage === undefined) {
      setInternalPage(1);
    }
  }, [filterText, DATA.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      const containers = document.querySelectorAll('.timeline-chart-container');
      containers.forEach(container => {
        const toolbar = container.querySelector('.canvasjs-chart-toolbar');
        if (!toolbar) return;

        const buttons = Array.from(toolbar.querySelectorAll('button'));
        // Find native toggle button (Pan/Zoom toggle). It is usually the first button before the reset button.
        const nativeToggle = buttons.find(b => {
          const state = b.getAttribute('state');
          const title = (b.getAttribute('title') || '').toLowerCase();
          return state === 'menu' || state === 'pan' || title === 'pan' || title === 'zoom';
        });

        if (!nativeToggle) return;

        let zoomBtn = toolbar.querySelector('.custom-zoom-btn') as HTMLButtonElement;
        let panBtn = toolbar.querySelector('.custom-pan-btn') as HTMLButtonElement;

        // Wait for buttons to be ready
        // triggerNativeToggle is removed here as it's redefined safely below.

        if (!zoomBtn || !panBtn) {
          if (!zoomBtn) {
            zoomBtn = document.createElement('button');
            zoomBtn.className = 'custom-zoom-btn';
            zoomBtn.setAttribute('title', 'Selecionar área de zoom');
            zoomBtn.innerHTML = '<svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" style="width:18px;height:18px;fill:currentColor;"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path></svg>';
            zoomBtn.style.padding = '4px';
            zoomBtn.style.cursor = 'pointer';
            zoomBtn.style.border = 'none';
            zoomBtn.style.display = 'inline-flex';
            zoomBtn.style.alignItems = 'center';
            zoomBtn.style.justifyContent = 'center';
            zoomBtn.style.height = '100%';
            zoomBtn.style.marginRight = '2px';
            zoomBtn.style.verticalAlign = 'top';
            toolbar.insertBefore(zoomBtn, nativeToggle);
          }
          if (!panBtn) {
            panBtn = document.createElement('button');
            panBtn.className = 'custom-pan-btn';
            panBtn.setAttribute('title', 'Mover gráfico');
            panBtn.innerHTML = '<svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" style="width:18px;height:18px;fill:currentColor;"><path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z"></path></svg>';
            panBtn.style.padding = '4px';
            panBtn.style.cursor = 'pointer';
            panBtn.style.border = 'none';
            panBtn.style.display = 'inline-flex';
            panBtn.style.alignItems = 'center';
            panBtn.style.justifyContent = 'center';
            panBtn.style.height = '100%';
            panBtn.style.marginRight = '2px';
            panBtn.style.verticalAlign = 'top';
            toolbar.insertBefore(panBtn, nativeToggle);
          }

          const getNativeToggle = () => {
            const btns = Array.from(toolbar.querySelectorAll('button'));
            return btns.find(b => {
              const s = b.getAttribute('state');
              const t = (b.getAttribute('title') || '').toLowerCase();
              return s === 'menu' || s === 'pan' || t === 'pan' || t === 'zoom';
            }) as HTMLButtonElement | undefined;
          };

          const isCurrentlyZoomMode = () => {
            const toggle = getNativeToggle();
            if (!toggle) return true; // Default to zoom if we can't tell
            const title = (toggle.getAttribute('title') || '').toLowerCase();
            const state = toggle.getAttribute('state');
            // If title is 'pan', it means the button is offering to switch to Pan mode. Therefore, current mode is Zoom.
            // If state is 'menu', that also typically corresponds to the Pan icon in CanvasJS.
            return title === 'pan' || state === 'menu';
          };

          // Custom buttons just need relative positioning to contain the absolute native button
          zoomBtn.style.position = 'relative';
          panBtn.style.position = 'relative';
        }

        const title = (nativeToggle.getAttribute('title') || '').toLowerCase();
        const state = nativeToggle.getAttribute('state');
        const isZoomActive = title === 'pan' || state === 'menu';
        const isPanActive = !isZoomActive;

        if (isZoomActive) {
          zoomBtn.style.backgroundColor = '#e2e8f0'; // pressed
          panBtn.style.backgroundColor = 'transparent';

          // We are in Zoom mode, so clicking Pan should toggle it.
          // Move the native toggle over the Pan button.
          nativeToggle.style.position = 'absolute';
          nativeToggle.style.top = '0';
          nativeToggle.style.left = '0';
          nativeToggle.style.width = '100%';
          nativeToggle.style.height = '100%';
          nativeToggle.style.opacity = '0';
          nativeToggle.style.zIndex = '9999';
          if (nativeToggle.parentElement !== panBtn) {
            panBtn.appendChild(nativeToggle);
          }
        } else {
          zoomBtn.style.backgroundColor = 'transparent';
          panBtn.style.backgroundColor = '#e2e8f0'; // pressed

          // We are in Pan mode, so clicking Zoom should toggle it.
          // Move the native toggle over the Zoom button.
          nativeToggle.style.position = 'absolute';
          nativeToggle.style.top = '0';
          nativeToggle.style.left = '0';
          nativeToggle.style.width = '100%';
          nativeToggle.style.height = '100%';
          nativeToggle.style.opacity = '0';
          nativeToggle.style.zIndex = '9999';
          if (nativeToggle.parentElement !== zoomBtn) {
            zoomBtn.appendChild(nativeToggle);
          }
        }

        const canvas = container.querySelector('.canvasjs-chart-canvas') as HTMLCanvasElement;
        if (canvas) {
          if (isZoomActive) {
            if (!canvas.classList.contains('force-zoom-cursor')) {
              canvas.classList.add('force-zoom-cursor');
            }
          } else {
            canvas.classList.remove('force-zoom-cursor');
          }
        }
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  const windowRange = normalizeDashboardRange(
    startDate ? new Date(`${startDate}T00:00:00`) : undefined,
    endDate ? new Date(`${endDate}T23:59:59.999`) : undefined,
  );
  const windowStart = windowRange.start.getTime();
  const windowEnd = windowRange.end.getTime();

  let maxDataTime = 0;
  DATA.forEach(d => {
    if (d.y[1] > maxDataTime) maxDataTime = d.y[1];
  });
  const nowMs = Math.max(currentNow, maxDataTime);
  const effectiveEnd = windowEnd > nowMs
    ? Math.max(windowStart, Math.min(windowEnd, nowMs))
    : windowEnd;
  const chartMax = Math.max(effectiveEnd, windowStart + 1000);

  const boundedData: TimelineRangeDataPoint[] = DATA
    .filter(point => point.y[1] > windowStart && point.y[0] < effectiveEnd)
    .map(point => ({ ...point, y: [Math.max(point.y[0], windowStart), Math.min(point.y[1], effectiveEnd)] }));
  const filteredDataPoints = filterText
    ? boundedData.filter(d => d.label && d.label.toLowerCase().includes(filterText.toLowerCase()))
    : boundedData;

  // Extract unique machines and their original x to maintain order
  const uniqueMachinesMap = new Map<number, string>();
  filteredDataPoints.forEach(d => {
    if (d.label && !uniqueMachinesMap.has(d.x)) {
      uniqueMachinesMap.set(d.x, d.label);
    }
  });

  // Sort by original x
  const uniqueMachines = Array.from(uniqueMachinesMap.keys()).sort((a, b) => a - b);

  const totalPages = externalTotalPages !== undefined ? externalTotalPages : Math.ceil(uniqueMachines.length / itemsPerPage);
  const paginatedMachineXValues = new Set(
    externalPage !== undefined 
      ? uniqueMachines // If externally paginated, DATA is already just the current page
      : uniqueMachines.slice((page - 1) * itemsPerPage, page * itemsPerPage)
  );

  const paginatedDataPoints = filteredDataPoints.filter(d => paginatedMachineXValues.has(d.x));

  const remappedData: TimelineRangeDataPoint[] = [];
  let currentNewX = 1;
  const xMapping = new Map<number, number>();

  paginatedDataPoints.forEach(d => {
    if (!xMapping.has(d.x)) {
      xMapping.set(d.x, currentNewX++);
    }
    remappedData.push({ ...d, x: xMapping.get(d.x)! });
  });

  const uniqueRows = xMapping.size || 1;
  const dynamicHeight = Math.max(400, uniqueRows * 26);

  // Compute stripLines for each shift time on each day in the date range
  const stripLines: any[] = [];

  if (remappedData.length > 0 && shiftTimes.length > 0) {
    let minTime = Infinity;
    let maxTime = -Infinity;
    remappedData.forEach(d => {
      if (d.y[0] < minTime) minTime = d.y[0];
      if (d.y[1] > maxTime) maxTime = d.y[1];
    });

    if (minTime < Infinity && maxTime > -Infinity) {
      const minDate = new Date(minTime);
      minDate.setHours(0, 0, 0, 0);
      const maxDate = new Date(Math.min(maxTime, effectiveEnd));
      maxDate.setHours(23, 59, 59, 999);

      for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
        shiftTimes.forEach(timeStr => {
          const parts = timeStr.split(':');
          if (parts.length >= 2) {
            const h = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10);
            if (!isNaN(h) && !isNaN(m)) {
              const shiftDate = new Date(d);
              shiftDate.setHours(h, m, 0, 0);

              if (shiftDate.getTime() >= windowStart && shiftDate.getTime() <= effectiveEnd) {
                stripLines.push({
                  value: shiftDate.getTime(),
                  color: "#9ca3af",
                  thickness: 1,
                  lineDashType: "solid"
                });
              }
            }
          }
        });
      }
    }
  }

  const chartOptions = {
    animationEnabled: true,
    zoomEnabled: true,
    zoomType: "xy",
    height: dynamicHeight,
    backgroundColor: "transparent",
    theme: isDark ? "dark2" : "light2",
    title: {
      text: "",
    },
    subtitles: shiftTimes.length > 0 ? [{
      text: `Horários de Turno (Linhas Verticais): ${shiftTimes.join(', ')}`,
      fontSize: 12,
      fontFamily: "Roboto, sans-serif",
      fontColor: isDark ? "#cbd5e1" : "#4b5563",
      padding: 10
    }] : [],
    axisY: {
      title: "",
      minimum: windowStart,
      maximum: chartMax,
      includeZero: false,
      stripLines: stripLines,
      gridThickness: 0,
      tickThickness: 0,
      labelAngle: -90,
      labelFontSize: 12,
      labelFontColor: isDark ? "#cbd5e1" : "#666666",
      labelFontFamily: "Roboto, sans-serif",
      labelFormatter: (e: any) => {
        if (!e.value) return "";
        const d = new Date(e.value);
        
        // If the interval is small (e.g. less than a day), show hours:mins
        // We can approximate by checking if it's not midnight or if we just want to always show time
        const isMidnight = d.getHours() === 0 && d.getMinutes() === 0;
        
        if (isMidnight) {
            return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else {
            return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        }
      },
      crosshair: {
        enabled: true,
        snapToDataPoint: false,
        labelFormatter: (e: any) => {
          if (!e.value) return "";
          const d = new Date(e.value);
          const day = String(d.getDate()).padStart(2, '0');
          const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
          const month = monthNames[d.getMonth()];
          const hours = String(d.getHours()).padStart(2, '0');
          const mins = String(d.getMinutes()).padStart(2, '0');
          const secs = String(d.getSeconds()).padStart(2, '0');
          return `${day} ${month} ${hours}:${mins}:${secs}`;
        }
      },
    },
    axisX: {
      title: "",
      interval: 1,
      minimum: 0, // Ensure row 0 is visible to show the labels
      labelFontSize: 12,
      labelFontColor: isDark ? "#cbd5e1" : "#333333",
      labelFontFamily: "Roboto, sans-serif",
      labelFontWeight: "normal",
      labelFormatter: (e: any) => {
        if (e.label) return e.label;
        if (e.value === 1) return "Eventos";
        if (e.value === 2) return "Heartbeats";
        return "";
      }
    },
    toolTip: {
      shared: false,
      fontFamily: "Roboto, sans-serif",
      backgroundColor: isDark ? "#162328" : "#ffffff",
      fontColor: isDark ? "#f1f5f9" : "#1e293b",
      borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#cbd5e1",
      contentFormatter: (e: any) => {
        const dp = e.entries[0].dataPoint;
        return dp.toolTipContent || "";
      }
    },
    data: [
      {
        type: "rangeBar",
        dataPointWidth: uniqueRows > 5 ? 12 : undefined,
        dataPoints: fillGaps ? fillTimelineGaps(remappedData, windowStart, effectiveEnd, STATE_COLORS['Desligado']) : remappedData,
      }
    ],
  };

  return (
    <div style={{ width: '100%' }} className="timeline-chart-container">
      <style>{`
        .timeline-chart-container .canvasjs-chart-toolbar {
          display: flex !important;
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 10 !important;
          align-items: center;
        }
        /* The native button is now nested inside the custom buttons, so we don't need to force hide it here. We handle it in JS. */
        .timeline-chart-container .canvasjs-chart-toolbar button {
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
        }
        .timeline-chart-container .force-zoom-cursor {
          cursor: crosshair !important;
        }
      `}</style>
      <div className='box-dash'>
        <Paper elevation={5} style={{ padding: "20px" }}>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', xl: '1fr auto 1fr' }} alignItems="center" mb={2} gap="15px">
            <Box>
              <h2 style={{ margin: 0 }}>{TITLE}</h2>
            </Box>
            <Box display="flex" justifyContent={{ xs: 'flex-start', xl: 'center' }}>
              {totalPages > 1 && (
                <Pagination count={totalPages} page={page} onChange={(_, value) => handlePageChange(value)} color="primary" />
              )}
            </Box>
            <Box display="flex" gap="15px" alignItems="center" justifyContent={{ xs: 'flex-start', xl: 'flex-end' }} flexWrap="wrap">
              {onDateRangeChange && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <label className="timeline-date-label" style={{ fontSize: '0.9rem', fontWeight: 600 }}>Início:</label>
                  <input
                    type="date"
                    className="timeline-date-input"
                    title="Limite máximo de pesquisa: 90 dias"
                    value={startDate || ''}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      if (!newStart) return;
                      if (endDate) {
                        const s = new Date(newStart);
                        const eD = new Date(endDate);
                        if (s > eD) {
                          onDateRangeChange(newStart, newStart);
                          return;
                        }
                        if ((eD.getTime() - s.getTime()) / (1000 * 3600 * 24) > 89) {
                          const newEnd = new Date(s);
                          newEnd.setDate(newEnd.getDate() + 89);
                          onDateRangeChange(newStart, newEnd.toISOString().split('T')[0]);
                          return;
                        }
                      }
                      onDateRangeChange(newStart, endDate || newStart);
                    }}
                    style={{ padding: '6px', borderRadius: '4px' }}
                  />
                  <label className="timeline-date-label" style={{ fontSize: '0.9rem', fontWeight: 600 }}>Fim:</label>
                  <input
                    type="date"
                    className="timeline-date-input"
                    title="Limite máximo de pesquisa: 90 dias"
                    value={endDate || ''}
                    min={startDate}
                    max={startDate ? new Date(new Date(startDate).getTime() + 89 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined}
                    onChange={(e) => {
                      const newEnd = e.target.value;
                      if (!newEnd) return;
                      if (startDate) {
                        const s = new Date(startDate);
                        const eD = new Date(newEnd);
                        if (eD < s) {
                          onDateRangeChange(newEnd, newEnd);
                          return;
                        }
                        if ((eD.getTime() - s.getTime()) / (1000 * 3600 * 24) > 89) {
                          const newStart = new Date(eD);
                          newStart.setDate(newStart.getDate() - 89);
                          onDateRangeChange(newStart.toISOString().split('T')[0], newEnd);
                          return;
                        }
                      }
                      onDateRangeChange(startDate || newEnd, newEnd);
                    }}
                    style={{ padding: '6px', borderRadius: '4px' }}
                  />
                </div>
              )}
              <input
                type="text"
                className="timeline-filter-input"
                placeholder="Filtrar máquinas..."
                value={filterText}
                onChange={(e) => handleFilterChange(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', minWidth: '200px' }}
              />
            </Box>
          </Box>
          <div style={{ height: dynamicHeight + 'px', width: '100%' }}>
            <CanvasJSReact.CanvasJSChart options={chartOptions} />
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default HeartbeatTimelineChart;
